/**
 * Verification of signed live-update (OTA) releases -- the device side.
 *
 * Signing lives in scripts/ota-signing.mjs (format documented there) and runs
 * only in .github/workflows/ota-release.yml. A device trusts nothing it
 * fetches from /ota/ until the Ed25519 signature checks out against a public
 * key compiled into this bundle (VITE_OTA_PUBLIC_KEY). Everything here fails
 * closed: no key, no WebCrypto, an unsigned or malformed file, a bad signature
 * or an out-of-range field all throw, and the caller downloads nothing.
 *
 * Ed25519 implementation: WebCrypto (`crypto.subtle`, algorithm 'Ed25519').
 * frontend/node_modules has no audited JS implementation (@noble/curves,
 * @noble/ed25519 and tweetnacl are all absent), and hand-rolled curve code is
 * not an acceptable substitute. Android System WebView supports Ed25519 in
 * WebCrypto from Chromium 137 and updates through Play; the app runs on
 * https://localhost (Capacitor's default scheme), a secure context, so
 * `crypto.subtle` exists. FALLBACK DECISION: a WebView without Ed25519 fails
 * closed -- importKey throws, the update is skipped, the device keeps its
 * current bundle and gets new code through the store channel. If field data
 * shows a meaningful population stuck there, add @noble/curves as a reviewed
 * dependency rather than writing curve arithmetic here.
 */

export const OTA_ENVELOPE_FORMAT = 'cmdbridge-ota-signed/1';
/** Must match OTA_SIGNATURE_CONTEXT in scripts/ota-signing.mjs. */
export const OTA_SIGNATURE_CONTEXT = 'cmdbridge-ota/v1\n';

export class OtaVerificationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OtaVerificationError';
  }
}

export interface OtaReleaseHeader {
  kind: 'version' | 'manifest';
  /** Git SHA of the bundle (its VITE_BUILD_SHA). */
  version: string;
  /** Monotonic: a device refuses any counter <= the highest it has installed. */
  release_counter: number;
  /** Devices that verify this release refuse every release below this from then on. */
  min_counter: number;
  nativeFingerprint: string;
  builtAt?: string;
  releasedAt?: string;
}

export interface OtaFileEntry {
  file_name: string;
  file_hash: string;
}

export interface OtaReleaseManifest extends OtaReleaseHeader {
  kind: 'manifest';
  files: OtaFileEntry[];
}

const FILE_HASH = /^[0-9a-f]{64}$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function base64ToBytes(text: string): Uint8Array<ArrayBuffer> {
  const binary = atob(text.trim()); // throws on invalid base64
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * Pinned public keys from VITE_OTA_PUBLIC_KEY: base64 raw 32-byte Ed25519
 * keys, comma- or space-separated (two during a key rotation). Malformed
 * entries are dropped; an empty result means OTA is disabled.
 */
export function parseOtaPublicKeys(raw: string | undefined | null): Uint8Array<ArrayBuffer>[] {
  if (!raw) return [];
  return raw
    .split(/[\s,]+/)
    .filter(Boolean)
    .flatMap((entry) => {
      try {
        const bytes = base64ToBytes(entry);
        return bytes.length === 32 ? [bytes] : [];
      } catch {
        return [];
      }
    });
}

/**
 * Verifies a signed envelope and returns its parsed payload. The signature is
 * checked over the exact payload text received; only then is it parsed.
 */
export async function verifyOtaEnvelope(
  envelope: unknown,
  publicKeys: Uint8Array<ArrayBuffer>[],
  subtle: SubtleCrypto | undefined = globalThis.crypto?.subtle,
): Promise<unknown> {
  if (publicKeys.length === 0) throw new OtaVerificationError('no OTA public key in this build');
  if (!subtle) throw new OtaVerificationError('WebCrypto is unavailable');
  if (
    !isRecord(envelope) ||
    envelope.format !== OTA_ENVELOPE_FORMAT ||
    typeof envelope.payload !== 'string' ||
    typeof envelope.signature !== 'string'
  ) {
    throw new OtaVerificationError('not a signed OTA envelope');
  }

  let signature: Uint8Array<ArrayBuffer>;
  try {
    signature = base64ToBytes(envelope.signature);
  } catch {
    throw new OtaVerificationError('signature is not base64');
  }
  if (signature.length !== 64) throw new OtaVerificationError('signature has the wrong length');

  const message = new TextEncoder().encode(OTA_SIGNATURE_CONTEXT + envelope.payload);
  for (const raw of publicKeys) {
    let key: CryptoKey;
    try {
      key = await subtle.importKey('raw', raw, { name: 'Ed25519' }, false, ['verify']);
    } catch {
      throw new OtaVerificationError('this WebView does not support Ed25519');
    }
    if (await subtle.verify({ name: 'Ed25519' }, key, signature, message)) {
      try {
        return JSON.parse(envelope.payload) as unknown;
      } catch {
        throw new OtaVerificationError('signed payload is not JSON');
      }
    }
  }
  throw new OtaVerificationError('signature does not match a trusted OTA key');
}

function isSafeRelativePath(name: unknown): name is string {
  return (
    typeof name === 'string' &&
    name.length > 0 &&
    !name.startsWith('/') &&
    !name.includes('\\') &&
    !name.split('/').some((seg) => seg === '' || seg === '.' || seg === '..')
  );
}

/** Shape-checks a verified payload. Signed is not the same as well-formed. */
export function parseOtaPayload(payload: unknown, kind: 'version'): OtaReleaseHeader;
export function parseOtaPayload(payload: unknown, kind: 'manifest'): OtaReleaseManifest;
export function parseOtaPayload(payload: unknown, kind: 'version' | 'manifest'): OtaReleaseHeader {
  if (!isRecord(payload) || payload.kind !== kind) {
    throw new OtaVerificationError(`signed payload is not a ${kind}`);
  }
  const { version, release_counter: counter, min_counter: min, nativeFingerprint } = payload;
  if (typeof version !== 'string' || !version) throw new OtaVerificationError('missing version');
  if (typeof nativeFingerprint !== 'string' || !nativeFingerprint) {
    throw new OtaVerificationError('missing nativeFingerprint');
  }
  if (typeof counter !== 'number' || !Number.isSafeInteger(counter) || counter < 1) {
    throw new OtaVerificationError('release_counter must be an integer >= 1');
  }
  if (typeof min !== 'number' || !Number.isSafeInteger(min) || min < 0 || min > counter) {
    throw new OtaVerificationError('min_counter must be an integer between 0 and release_counter');
  }
  const header: OtaReleaseHeader = {
    kind,
    version,
    release_counter: counter,
    min_counter: min,
    nativeFingerprint,
    builtAt: typeof payload.builtAt === 'string' ? payload.builtAt : undefined,
    releasedAt: typeof payload.releasedAt === 'string' ? payload.releasedAt : undefined,
  };
  if (kind === 'version') return header;

  const { files } = payload;
  if (!Array.isArray(files) || files.length === 0) throw new OtaVerificationError('manifest has no files');
  const entries: OtaFileEntry[] = files.map((f: unknown) => {
    if (!isRecord(f) || !isSafeRelativePath(f.file_name) || typeof f.file_hash !== 'string' || !FILE_HASH.test(f.file_hash)) {
      throw new OtaVerificationError('manifest has an invalid file entry');
    }
    return { file_name: f.file_name, file_hash: f.file_hash };
  });
  if (!entries.some((f) => f.file_name === 'index.html')) {
    throw new OtaVerificationError('manifest has no index.html');
  }
  return { ...header, kind: 'manifest', files: entries } as OtaReleaseManifest;
}
